<template>
  <div>
    <section>
      <template>
        <v-container fluid>
          <v-row class="match-height">
            <v-col
              cols="12"
              :sm="$containerBreakpoint.smAndUp ? '12' : '12'"
              :md="$containerBreakpoint.mdAndUp ? '6' : '12'"
            >
              <v-card>
                <v-card-title class="body-2 v-card__subtitle"
                  ><v-icon left small class="mr-2">mdi-label</v-icon
                  >Qualifkationsübersicht (Nur höchste)
                </v-card-title>
                <v-card-text>
                  <div v-if="!relevantSkills.length">
                    Keine Qualifikationen zugewiesen
                  </div>

                  <data-chip
                    v-else
                    v-for="(skill, index) in relevantSkills"
                    :key="index"
                    :color="skill.group.color"
                    :iconLeft="skill.group.icon"
                    :text="skill.title"
                    class="mr-2 mb-2"
                  >
                  </data-chip>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              :sm="$containerBreakpoint.smAndUp ? '12' : '12'"
              :md="$containerBreakpoint.mdAndUp ? '6' : '12'"
            >
              <v-card>
                <personnel-record-detail-card-content-fillable-positions
                  :member="{ fillablePositions: { data: fillablePositions } }"
                ></personnel-record-detail-card-content-fillable-positions>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card>
                <personnel-record-skills-data-table
                  :skills="skills"
                  mode="view"
                >
                </personnel-record-skills-data-table>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card>
                <personnel-record-courses-data-table
                  :courses="courses"
                  mode="view"
                >
                </personnel-record-courses-data-table>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </section>
  </div>
</template>

<script>
import { db } from "@/firebase";

import PersonnelRecordCoursesDataTable from "@/components/member-management/personnel-records/PersonnelRecordCoursesDataTable.vue";
import PersonnelRecordSkillsDataTable from "@/components/member-management/personnel-records/PersonnelRecordSkillsDataTable.vue";
import PersonnelRecordDetailCardContentFillablePositions from "@/components/member-management/personnel-records/PersonnelRecordDetailCardContentFillablePositions.vue";
import DataChip from "@/components/_systemwide/DataChip.vue";
import { AVAILABILITY, MEMBER_MANAGEMENT } from "@/store/modules.js";
import { GET_POSITIONS, GET_SKILLS } from "@/store/action-types.js";
import containerBreakpoints from "@/mixins/containerBreakpoints.js";

export default {
  name: "member-management-personnel-records-details-certificates",
  mixins: [containerBreakpoints],
  components: {
    DataChip,
    PersonnelRecordDetailCardContentFillablePositions,
    PersonnelRecordCoursesDataTable,
    PersonnelRecordSkillsDataTable,
  },
  props: {
    personnelRecord: Object,
    propContainerWidth: {
      type: Number,
      default: 0,
    },
    propPersonnelRecordId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      courses: [],
      skills: [],
      deadlines: [],
      showDeadlines: false,
    };
  },
  created() {
    this.initialize();
    this.deadlines = [
      {
        type: "G26.3",
        deadline: null,
      },
      {
        type: "Ausbildung",
        deadline: null,
      },
      {
        type: "ANT-Nachweis (Training)",
        deadline: null,
      },
    ];
  },
  mounted() {
    // Only initialize container breakpoints if not in split pane context
    // (propContainerWidth watcher handles split pane case)
    if (!(this.propContainerWidth > 0)) {
      this.initContainerBreakpoints(this.$el);
    }
  },
  computed: {
    positions() {
      const positions = this.$store.state.availability.positions;
      return positions.sort((a, b) => a.priority - b.priority);
    },
    skillGroups() {
      const skillGroups = this.$store.state.memberManagement.skillGroups;
      return skillGroups.sort((a, b) => a.sortKey - b.sortKey);
    },
    allSkills() {
      return this.$store.state.memberManagement.skills;
    },
    fillablePositions() {
      // console.log("Computing fillable positions...");

      const allPositions = this.positions;

      const validSkillIds = this.skills.reduce((acc, skill) => {
        const status = this.getSkillStatus(
          skill.qualification.since,
          skill.qualification.until
        );
        // console.log(`Skill ID: ${skill.skill.id}, Status: ${status}`);
        if (status === "active") {
          acc.push(skill.skill.id);
        }
        return acc;
      }, []);
      // console.log("Valid skill IDs:", validSkillIds); // error: this returns an array, but some item is undefined

      const fillablePositions = allPositions.filter((position) => {
        const conjunctionType = position.preconditions.conjunctionType;
        const requiredSkillIds = position.preconditions.skillIds;

        // console.log(
        //   `Position: ${position.title}, Conjunction Type: ${conjunctionType}, Required Skill IDs: ${requiredSkillIds}`
        // );

        if (conjunctionType === "AND") {
          const result = requiredSkillIds.every((skillId) =>
            validSkillIds.includes(skillId)
          );
          // console.log(
          //   `AND check result for position "${position.title}":`,
          //   result
          // );
          return result;
        } else if (conjunctionType === "OR") {
          const result = requiredSkillIds.some((skillId) =>
            validSkillIds.includes(skillId)
          );
          // console.log(
          //   `OR check result for position "${position.title}":`,
          //   result
          // );
          return result;
        } else {
          // console.log(
          //   `Unknown conjunction type for position "${position.title}"`
          // );
          return false;
        }
      });

      // console.log("Fillable positions:", fillablePositions);
      return fillablePositions;
    },
    relevantSkills() {
      // Step 1: Filter out the active skills
      const activeSkills = this.skills.filter(
        (skill) =>
          this.getSkillStatus(
            skill.qualification.since,
            skill.qualification.until
          ) === "active"
      );

      // Step 2: Group the active skills by their group.id
      const skillGroups = new Map();
      activeSkills.forEach((skill) => {
        const groupId = skill.skill.group.id;
        if (!skillGroups.has(groupId)) {
          skillGroups.set(groupId, []);
        }
        skillGroups.get(groupId).push(skill);
      });

      // Step 3: For each group, add the relevant skills to relevantSkills
      let relevantSkills = [];
      for (let skills of skillGroups.values()) {
        if (skills[0].skill.group.skillsAreEqual) {
          relevantSkills.push(...skills);
        } else {
          const highestSortKeySkill = skills.reduce((highest, skill) =>
            highest.skill.sortKey > skill.skill.sortKey ? highest : skill
          );
          relevantSkills.push(highestSortKeySkill);
        }
      }

      // Step 4: Sort relevantSkills by sortKey
      relevantSkills.sort(
        (a, b) => a.skill.group.sortKey - b.skill.group.sortKey
      );

      return relevantSkills.map((skill) => skill.skill);
    },
    // Use prop ID if available (split screen mode), otherwise fall back to route param
    personnelRecordId() {
      return this.propPersonnelRecordId || this.$route.params.itemId;
    },
  },
  watch: {
    $route: "initialize",
    // Watch prop changes from parent component
    propContainerWidth: {
      immediate: true,
      handler(newWidth) {
        if (newWidth > 0) {
          // Only update if the breakpoint would change (to prevent unnecessary re-renders)
          const newBreakpoint = this._getBreakpointForWidth(newWidth);
          const currentBreakpoint = this._getBreakpointForWidth(
            this.containerWidth || 0
          );
          if (
            newBreakpoint !== currentBreakpoint ||
            this.containerWidth === 0
          ) {
            this.containerWidth = newWidth;
          }
        }
      },
    },
  },
  methods: {
    getSkillStatus(qualificationSince, qualificationUntil) {
      const currentDate = new Date();
      const sinceDate = qualificationSince ? qualificationSince.toDate() : null;
      const untilDate = qualificationUntil ? qualificationUntil.toDate() : null;

      if (sinceDate && sinceDate > currentDate) {
        return "upcoming";
      } else if (untilDate && untilDate < currentDate) {
        return "expired";
      } else {
        return "active";
      }
    },
    initialize() {
      this.getCourses();
      this.getSkills();
      this.$store.dispatch(`${AVAILABILITY}${GET_POSITIONS}`, {
        organizationId: this.$route.params.organizationId,
      });
      this.$store.dispatch(`${MEMBER_MANAGEMENT}${GET_SKILLS}`, {
        organizationId: this.$route.params.organizationId,
      });
    },
    getCourses() {
      console.log("loading from db - courses");
      this.courses = [];
      return db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("personnelRecords_courses")
        .where("userId", "==", this.personnelRecordId)
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            this.courses.push(doc.data());
          });
        })
        .catch(function (error) {
          console.log("Error getting document:", error);
        });
    },
    getSkills() {
      console.log("loading from db - skills");
      this.skills = [];
      return db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("personnelRecords_skills")
        .where("userId", "==", this.personnelRecordId)
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            this.skills.push(doc.data());
          });
        })
        .catch(function (error) {
          console.log("Error getting document:", error);
        });
    },
  },
};
</script>

<style></style>
